import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { USERS_API } from "./api";
import { addNewMessage, setUserInfo } from "./slice";
import { useAuth, useCurrentState } from "./hooks";
import { RemainedSecondProvider } from "./context";
import Layout from "./components/Layout";
import SignIn from "./pages/SignIn";
import ForgetPassword from "./pages/ForgetPassword";
import Otp from "./pages/Otp";
import NewPassword from "./pages/NewPassword";
import globalTheme from "./Theme/Theme";
import SignUp from "./pages/SignUp";
import Terms from "./pages/Terms";
import PersonalInformation from "./pages/PersonalInformation";
import SignUpResult from "./pages/SignUpResult";
import Home from "./pages/Home";
import Welcome from "./pages/Welcome";
import SelectCenter from "./pages/SelectCenter";
import SearchAndCandidates from "./pages/Search&Candidates";
import StartSearching from "./pages/StartSearching";
import Profile from "./pages/Profile";
import Edit from "./pages/Edit";
import Deactivate from "./pages/Deactivate";
import Ticketing from "./pages/Ticketing";
import RegisterComplaints from "./pages/RegisterComplaints";
import DeactivateResult from "./pages/DeactivateResult";
import Questions from "./pages/Questions";
import EducationAndAdvice from "./pages/EducationAndAdvice";
import Messages from "./pages/Messages";
import PsychologyTests from "./pages/PsychologyTests";
import CenterProfile from "./pages/CenterProfile";
import GenderDetermination from "./pages/GenderDetermination";
import InfoStatus from "./pages/InfoStatus";
import CandidateInfo from "./components/candidates/CandidateInfo";
import Alert from "./components/alerts/Alert";
import Wait from "./components/Wait";
import WaitForCandidate from "./components/WaitForCandidate";
import TicketingChat from "./components/ticketing/TicketingChat";
import CandidateDetails from "./pages/CandidateDetails";
import CandidateInformation from "./pages/CandidateInformation";
import AboutUs from "./pages/AboutUs";
import { ReactComponent as FirstAnimationBirds } from "./assets/logo/FirstAnimationBirds.svg";
import { ReactComponent as Logo } from "./assets/logo/Logo.svg";
import "./App.css";

function App() {
  const dispatch = useDispatch();
  const { isLoggedIn } = useAuth();
  const updateUserStatus = useCurrentState();
  const { data: userCurrentState, isNewUser } = useSelector((state) => state.UserCurrentState);
  const { messages } = useSelector((state) => state.Messages);
  const [isAnimationShown, setIsAnimationShown] = useState(
    window.sessionStorage.getItem("isAnimationShown") !== null
  );
  let theme = createTheme(globalTheme);

  const handleSearchCandidateAccess = (code) => {
    let accessCodes = [8, 65, 70, 50];
    if (code) {
      return accessCodes.includes(parseInt(code));
    }
    return false;
  };

  useQuery(["getUserInfo"], () => USERS_API.GetUserInfo(), {
    onSuccess: ({ data }) => {
      if (data.success) {
        dispatch(setUserInfo(data.data));
      } else {
        dispatch(
          addNewMessage({
            id: "failed-on-get-dashboard-messages",
            text: "مشکلی پیش  آمده مجدد تلاش کنید.",
            type: "error",
          })
        );
      }
    },
    onError: () => {
      dispatch(
        addNewMessage({
          id: "failed-on-get-dashboard-messages",
          text: "مشکلی پیش  آمده مجدد تلاش کنید.",
          type: "error",
        })
      );
    },
    enabled: isLoggedIn,
  });

  useEffect(() => {
    if (isLoggedIn) {
      updateUserStatus();
    }
  }, [updateUserStatus, isLoggedIn]);

  useEffect(() => {
    let animationTimer;
    if (!isAnimationShown) {
      animationTimer = setTimeout(() => {
        window.sessionStorage.setItem("isAnimationShown", true);
        setIsAnimationShown(true);
      }, 5700);
    }
    return () => {
      clearTimeout(animationTimer);
    };
  }, [isAnimationShown]);

  return (
    <ThemeProvider theme={theme}>
      <Alert messages={messages} />
      {!isAnimationShown && (
        <div className="animation-container">
          <Logo className="logo" />
          <div className="first-loading">
            <FirstAnimationBirds className="first-loading--birds" />
          </div>
        </div>
      )}
      {!isLoggedIn && (
        <>
          <RemainedSecondProvider>
            <Routes>
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/sign-in/forget-password" element={<ForgetPassword />} />
              <Route path="/verification-code" element={<Otp />} />
              <Route path="/sign-in/new-password" element={<NewPassword />} />
              <Route path="/sign-up" element={<SignUp />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/sign-up/result" element={<SignUpResult />} />
              <Route path="gender-determination" element={<GenderDetermination />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="*" element={<Navigate replace to="/sign-in" />} />
            </Routes>
          </RemainedSecondProvider>
        </>
      )}
      {isLoggedIn && (
        <>
          <Layout>
            <Routes>
              {isNewUser && (
                <>
                  <Route path="/welcome" element={<Welcome />} />
                  <Route path="/center" element={<SelectCenter />} />
                  <Route path="/center-profile/:branchId" element={<CenterProfile />} />
                  <Route path="*" element={<Navigate replace to="/welcome" />} />
                </>
              )}
              {!isNewUser && (
                <>
                  <Route path="/home" element={<Home />} />
                  <Route path="/personal-info/*" element={<PersonalInformation />} />
                  {handleSearchCandidateAccess(userCurrentState?.code) && (
                    <>
                      <Route path="/search-candidates" element={<StartSearching />} />
                      <Route path="/search-candidates/*" element={<SearchAndCandidates />} />
                    </>
                  )}
                  <Route path="/candidate/:id" element={<CandidateInformation />} />
                  <Route path="/candidate-details/:id" element={<CandidateDetails />} />
                  <Route path="/center-profile/:branchId" element={<CenterProfile />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/ticketing" element={<Ticketing />} />
                  <Route path="/ticketing-chat/:id" element={<TicketingChat />} />
                  <Route path="/complaints/:id" element={<RegisterComplaints />} />
                  <Route path="/deactivate/result" element={<DeactivateResult />} />
                  {/* <Route path="/questions" element={<Questions />} /> */}
                  {/* <Route path="/consulting" element={<EducationAndAdvice />} /> */}
                  <Route path="/messages" element={<Messages />} />
                  {/* <Route path="/tests" element={<PsychologyTests />} /> */}
                  <Route path="/wait" element={<Wait />} />
                  <Route path="/wait-for-candidate" element={<WaitForCandidate />} />
                  <Route path="*" element={<Navigate replace to="/home" />} />
                </>
              )}
            </Routes>
          </Layout>
        </>
      )}
    </ThemeProvider>
  );
}

export default App;
